/**
 * Re-orders the values of any "Size"-type option in a <variant-picker> by
 * their leading numeric component so reel sizes sort sensibly:
 *
 *   1000, 2500HG, C3000HG, 4000XG, 5000XG, 8500, 10500
 *
 * (not alphabetical: 10500, 1000, 2500HG, C3000HG, 4000XG …)
 *
 * Letter prefixes are ignored when computing the key (C3000HG → 3000),
 * so a value with a `C` prefix lands in the same numeric slot as its
 * non-prefixed siblings. Values without any digits are left in their
 * original relative order at the end of the list.
 *
 * Works for both:
 *   - dropdown style: <custom-select><ul role="listbox"><li data-value="..."/></ul></custom-select>
 *     plus the matching <select> used for no-JS / form submission.
 *   - listed (bubble) style: <input.opt-btn> + <label> pairs inside .option-selector__btns
 *
 * Only sorts when the option name contains "size" AND at least one value
 * contains a digit — otherwise alpha sizes like Small/Medium/Large are
 * left alone.
 */
(() => {
  const SIZE_NAME_RE = /size/i;

  /** Extract the first run of digits as an integer; Infinity if none. */
  function numKey(value) {
    const m = String(value).match(/\d+/);
    return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
  }

  /** Stable comparator: numeric key first, then full-string tiebreak. */
  function compareValues(a, b) {
    const na = numKey(a);
    const nb = numKey(b);
    if (na !== nb) return na - nb;
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  }

  function sortDropdown(selector) {
    const customSelect = selector.querySelector('custom-select');
    if (!customSelect) return;

    const listbox = customSelect.querySelector('[role="listbox"]');
    if (listbox) {
      const items = [...listbox.children];
      items
        .map((el) => ({ el, value: el.dataset.value || el.textContent.trim() }))
        .sort((a, b) => compareValues(a.value, b.value))
        .forEach(({ el }) => listbox.appendChild(el));
    }

    // Native <select> fallback used by the form when JS is off and by
    // some Shopify form handling — keep it in sync.
    const nativeSelect = customSelect.querySelector('select');
    if (nativeSelect) {
      const opts = [...nativeSelect.options];
      opts
        .sort((a, b) => compareValues(a.value, b.value))
        .forEach((o) => nativeSelect.appendChild(o));
    }
  }

  function sortListed(selector) {
    const container = selector.querySelector('.option-selector__btns');
    if (!container) return;

    const inputs = [...container.querySelectorAll('input.opt-btn')];
    const groups = inputs.map((input) => {
      const label = input.id ? container.querySelector(`label[for="${input.id}"]`) : null;
      return { input, label, value: input.value };
    });

    groups
      .sort((a, b) => compareValues(a.value, b.value))
      .forEach(({ input, label }) => {
        container.appendChild(input);
        if (label) container.appendChild(label);
      });
  }

  function hasNumericValue(selector) {
    const probe = [
      ...selector.querySelectorAll('input.opt-btn'),
      ...selector.querySelectorAll('option'),
      ...selector.querySelectorAll('[role="option"]'),
    ];
    return probe.some((el) => /\d/.test(el.value || el.dataset?.value || el.textContent || ''));
  }

  function sortSelector(selector) {
    const optName = selector.dataset.option || '';
    if (!SIZE_NAME_RE.test(optName)) return;
    if (!hasNumericValue(selector)) return;

    if (selector.dataset.selectorType === 'dropdown') {
      sortDropdown(selector);
    } else {
      sortListed(selector);
    }
  }

  function init() {
    document.querySelectorAll('.option-selector').forEach(sortSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.variantSizeSort = { init };
})();
